import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, DollarSign, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';
import SuccessModal from '../components/SuccessModal';

export const OffersPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [clientInfo, setClientInfo] = useState({
    client_name: '',
    client_email: '',
    client_phone: ''
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await api.get('/offers');
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (offer) => {
    setSelectedOffer(offer);
    setFormData({});
    setClientInfo({ client_name: '', client_email: '', client_phone: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post(`/offers/${selectedOffer.id}/subscribe`, {
        offer_id: selectedOffer.id,
        form_data: formData,
        client_name: clientInfo.client_name,
        client_email: clientInfo.client_email,
        client_phone: clientInfo.client_phone
      });

      setShowModal(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Erreur lors de la souscription');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field) => {
    switch (field.field_type) {
      case 'select':
        return (
          <Select 
            value={formData[field.id] || ''} 
            onValueChange={(value) => setFormData({...formData, [field.id]: value})}
          >
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`field-${field.id}`}>
              <SelectValue placeholder={field.placeholder || 'Sélectionner'} />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10">
              {field.options?.map((option, i) => (
                <SelectItem key={i} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        return (
          <div className="space-y-2">
            {field.options?.map((option, i) => (
              <label key={i} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={(formData[field.id] || []).includes(option)}
                  onChange={(e) => {
                    const current = formData[field.id] || [];
                    if (e.target.checked) {
                      setFormData({...formData, [field.id]: [...current, option]});
                    } else {
                      setFormData({...formData, [field.id]: current.filter(o => o !== option)});
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-white">{option}</span>
              </label>
            ))}
          </div>
        );
      case 'textarea':
        return (
          <Textarea
            value={formData[field.id] || ''}
            onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
            placeholder={field.placeholder}
            required={field.required}
            rows={3}
            className="bg-white/5 border-white/10 text-white"
            data-testid={`field-${field.id}`}
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={formData[field.id] || ''}
            onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
            required={field.required}
            className="bg-white/5 border-white/10 text-white"
            data-testid={`field-${field.id}`}
          />
        );
      default:
        return (
          <Input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
            value={formData[field.id] || ''}
            onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
            placeholder={field.placeholder}
            required={field.required}
            className="bg-white/5 border-white/10 text-white"
            data-testid={`field-${field.id}`}
          />
        );
    }
  };

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Hero Section */}
      <section className="pt-6 pb-4 relative" data-testid="offers-hero">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </button>
          
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase">Offres Spéciales</span>
              <div className="h-px w-12 bg-[#D4AF37]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
              Nos <span className="text-[#D4AF37] italic">Offres</span>
            </h1>
            <p className="text-base text-[#94A3B8] max-w-xl mx-auto">
              Découvrez nos offres exclusives et promotions du moment
            </p>
          </motion.div>
        </div>
      </section>

      {/* Offers Grid */}
      <section className="py-8 relative" data-testid="offers-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-2 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card-luxury h-40 animate-pulse">
                  <div className="h-full bg-white/5" />
                </div>
              ))}
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
              <p className="text-[#94A3B8] text-lg mb-4">Aucune offre disponible pour le moment</p>
              <Link to="/contact">
                <button className="btn-gold px-6 py-3">Nous Contacter</button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-5">
              {offers.map((offer, index) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="card-luxury overflow-hidden flex" data-testid={`offer-${offer.id}`}>
                    {offer.image_url && (
                      <div className="w-40 h-40 flex-shrink-0 overflow-hidden">
                        <img 
                          src={offer.image_url} 
                          alt={offer.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-5 flex flex-col justify-center flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{offer.title}</h3>
                      <p className="text-[#94A3B8] text-sm line-clamp-2 mb-3">{offer.description}</p>
                      
                      <div className="flex items-center justify-between">
                        {offer.price && (
                          <span className="text-xl font-bold text-[#D4AF37]">
                            {formatPrice(offer.price, offer.currency)}
                          </span>
                        )}
                        <button 
                          className="btn-gold py-2 px-5 text-sm flex items-center gap-2"
                          onClick={() => handleSubscribe(offer)}
                          data-testid={`subscribe-${offer.id}`}
                        >
                          Souscrire
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscription Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-sm">Souscrire à l'offre</DialogTitle>
            <DialogDescription className="text-[#94A3B8] text-xs">
              {selectedOffer?.title}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="offer-subscription-form">
            {/* Client Info */}
            <div className="space-y-3 pb-3 border-b border-white/10">
              <h4 className="font-medium text-white text-xs">Vos Informations</h4>
              <div>
                <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                <Input
                  value={clientInfo.client_name}
                  onChange={(e) => setClientInfo({...clientInfo, client_name: e.target.value})}
                  required
                  placeholder="Votre nom complet"
                  className="bg-white/5 border-white/10 text-white text-xs h-9"
                  data-testid="client-name"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Email *</Label>
                <Input
                  type="email"
                  value={clientInfo.client_email}
                  onChange={(e) => setClientInfo({...clientInfo, client_email: e.target.value})}
                  required
                  placeholder="votre@email.com"
                  className="bg-white/5 border-white/10 text-white text-xs h-9"
                  data-testid="client-email"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Téléphone</Label>
                <Input
                  value={clientInfo.client_phone}
                  onChange={(e) => setClientInfo({...clientInfo, client_phone: e.target.value})}
                  placeholder="+243..."
                  className="bg-white/5 border-white/10 text-white text-xs h-9"
                  data-testid="client-phone"
                />
              </div>
            </div>

            {/* Dynamic Form Fields */}
            {selectedOffer?.form_fields?.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-white text-xs">Informations Supplémentaires</h4>
                {selectedOffer.form_fields.map((field) => (
                  <div key={field.id}>
                    <Label className="text-[#94A3B8] text-xs">
                      {field.label} {field.required && '*'}
                    </Label>
                    {renderFormField(field)}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-3">
              <button 
                type="button" 
                className="flex-1 btn-glass py-2.5 text-white text-xs"
                onClick={() => setShowModal(false)}
              >
                Annuler
              </button>
              <button 
                type="submit" 
                className="flex-1 btn-gold py-2.5 text-xs"
                disabled={submitting}
                data-testid="submit-subscription"
              >
                {submitting ? 'Envoi...' : 'Confirmer'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)}
        title="Souscription envoyée !"
        message="Votre souscription à l'offre a été enregistrée avec succès. Vous recevrez un email de confirmation."
      />
    </div>
  );
};

export default OffersPage;
